import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  shareReplay,
} from 'rxjs';
import { connect } from '@ardoq/rxbeach';
import {
  createViewStatusReporter,
  registerWhileSubscribed,
  withDataWork,
} from 'tabview/activeViewsStatus/viewStatusReporter';
import type { HasDataWorkToken } from 'tabview/activeViewsStatus/types';
import { interactiveRelationshipsViewLegendState$ } from 'tabview/relationshipDiagrams/interactiveRelationshipsViewLegendState$';
import { ViewModelStreamConfig } from './types';
import {
  RelationshipsRenderingDataLayer,
  type RelationshipsRenderingDataLayerProps,
} from './RelationshipsRenderingDataLayer';
import { BuildRelationshipsViewRenderingDataSourceArgs } from './viewModel$/buildRelationshipsViewRenderingData';
import { GetWorkspaceModeSourceDataArgs } from './viewModel$/getWorkspaceModeSourceData$';
import { GetViewpointModeSourceDataArgs } from './viewModel$/getViewpointModeSourceData$';
import { createRelationshipsViewCommands } from './commands';
import { Features, featureOperations } from '@ardoq/features';
import { featureContext$ } from 'streams/featureContext$';
import { isDiscoverJourney$ } from 'canvas/isDiscoverJourney';
import { isPresentationMode } from 'env';

const relationshipsViewCommands$ = combineLatest([
  isDiscoverJourney$,
  featureContext$,
]).pipe(
  map(
    ([isDiscoverJourney, featureContext]) =>
      isDiscoverJourney ||
      featureOperations.hasFeature(featureContext, Features.CANVAS_PROTOTYPE)
  ),
  distinctUntilChanged(),
  map(isQuickExploreSelectionEnabled =>
    createRelationshipsViewCommands({
      isPresentationMode: isPresentationMode(),
      isQuickExploreSelectionEnabled,
    })
  ),
  shareReplay({ bufferSize: 1, refCount: true })
);

const splitDataWorkToken = <TSourceData extends object>({
  dataWorkToken,
  ...sourceData
}: TSourceData & HasDataWorkToken) => ({ sourceData, dataWorkToken });

type ConnectRelationshipsRenderingDataLayerArgs<
  TSourceData extends BuildRelationshipsViewRenderingDataSourceArgs,
> = {
  getViewModelSourceData$: (
    args: GetWorkspaceModeSourceDataArgs | GetViewpointModeSourceDataArgs
  ) => Observable<TSourceData>;
  viewModelStreamConfig: ViewModelStreamConfig;
  isViewpointMode: boolean; // temporary, to be removed
};
const connectRelationshipsRenderingDataLayer = <
  TSourceData extends BuildRelationshipsViewRenderingDataSourceArgs,
>({
  getViewModelSourceData$,
  viewModelStreamConfig,
  isViewpointMode,
}: ConnectRelationshipsRenderingDataLayerArgs<TSourceData>) => {
  const viewStatusReporter = createViewStatusReporter(
    viewModelStreamConfig.viewInstanceId
  );
  const sourceData$ = getViewModelSourceData$({
    ...viewModelStreamConfig,
    viewStatusReporter,
  }).pipe(withDataWork(viewStatusReporter, 'source'), map(splitDataWorkToken));

  const viewModelInputs$ = combineLatest({
    sourceDataWithWork: sourceData$,
    viewSettingsForRenderingData:
      viewModelStreamConfig.viewSettingsForRenderingData$,
    interactiveLegendState: interactiveRelationshipsViewLegendState$,
  });

  const renderingDataLayerProps$: Observable<RelationshipsRenderingDataLayerProps> =
    combineLatest({
      viewModelInputs: viewModelInputs$,
      uiPropsSettings: viewModelStreamConfig.uiPropsSettings$,
      relationshipsViewCommands: relationshipsViewCommands$,
    }).pipe(
      map(
        ({
          viewModelInputs: {
            sourceDataWithWork: { sourceData, dataWorkToken },
            ...viewModelInputs
          },
          uiPropsSettings,
          relationshipsViewCommands,
        }) => ({
          ...viewModelInputs,
          sourceData,
          dataWorkToken,
          viewStatusReporter,
          uiPropsSettings,
          relationshipsViewCommands,
          isViewpointMode,
        })
      ),
      registerWhileSubscribed(viewStatusReporter)
    );

  return connect(RelationshipsRenderingDataLayer, renderingDataLayerProps$);
};

export { connectRelationshipsRenderingDataLayer };
